/**
 * Map Provider Abstraction Layer
 * Supports both Google Maps and Mapbox with the same interface
 */

// Base class for map providers
class MapProvider {
  constructor() {
    this.map = null;
    this.markers = [];
  }

  // Abstract methods to be implemented by each provider
  async initialize(element, options) {
    throw new Error("initialize() must be implemented");
  }

  createMarker(location, type) {
    throw new Error("createMarker() must be implemented");
  }

  createPopup(marker, location) {
    throw new Error("createPopup() must be implemented");
  }

  clearMarkers() {
    throw new Error("clearMarkers() must be implemented");
  }

  setCenter(lat, lng) {
    throw new Error("setCenter() must be implemented");
  }

  setZoom(zoom) {
    throw new Error("setZoom() must be implemented");
  }

  flyTo(lat, lng, zoom, callback) {
    throw new Error("flyTo() must be implemented");
  }

  addPolygon(coordinates, options) {
    throw new Error("addPolygon() must be implemented");
  }

  removePolygon(polygon) {
    throw new Error("removePolygon() must be implemented");
  }

  onIdle(callback) {
    throw new Error("onIdle() must be implemented");
  }

  fitBounds(bounds) {
    throw new Error("fitBounds() must be implemented");
  }

  disableTouchGestures() {
    // Override per provider
  }

  enableTouchGestures() {
    // Override per provider
  }

  /**
   * Wait for an external map library (Mapbox GL JS / Google Maps) to become
   * available before initializing. Caching/optimization plugins (e.g. WP Rocket
   * "Load JavaScript deferred") can defer the library script, so the plugin's
   * own init code may run before window.mapboxgl / window.google exists.
   * Instead of failing immediately, poll for the library for a short window.
   *
   * @param {Function} isReady - returns true once the library is available
   * @param {string}   name    - human-readable library name for the error
   * @param {number}   timeout - max time to wait, in ms (default 10s)
   * @param {number}   interval- poll interval, in ms (default 50ms)
   * @returns {Promise<void>}
   */
  waitForLibrary(isReady, name, timeout = 10000, interval = 50) {
    return new Promise((resolve, reject) => {
      if (isReady()) {
        resolve();
        return;
      }
      console.log(`⏳ Waiting for ${name} to load...`);
      const start = Date.now();
      const timer = setInterval(() => {
        if (isReady()) {
          clearInterval(timer);
          console.log(`✅ ${name} is now available`);
          resolve();
        } else if (Date.now() - start >= timeout) {
          clearInterval(timer);
          reject(new Error(`${name} failed to load. Please refresh the page.`));
        }
      }, interval);
    });
  }
}

// Google Maps Provider
class GoogleMapsProvider extends MapProvider {
  constructor() {
    super();
    this.infoWindow = null;
    this.currentPolygon = null;
  }

  async initialize(element, options) {
    console.log("🗺️ Initializing Google Maps...");

    // Wait for the Google Maps API — it may be deferred by a caching/optimization plugin
    await this.waitForLibrary(
      () => !!(window.google && window.google.maps),
      "Google Maps API",
    );

    // Check if Google Maps is loaded multiple times
    const multipleLoads =
      document.querySelectorAll('script[src*="maps.googleapis.com"]').length >
      1;

    // Use Standard Markers if multiple loads detected
    this.useAdvancedMarkers =
      !multipleLoads && !!window.google?.maps?.marker?.AdvancedMarkerElement;

    console.log(
      this.useAdvancedMarkers
        ? "✅ Using Advanced Markers"
        : "⚠️ Using Standard Markers (safer)",
    );

    const mapOptions = {
      zoom: options.zoom || 5,
      center: { lat: options.center.lat, lng: options.center.lng },
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: options.fullscreenControl !== false,
      gestureHandling: "auto",
      zoomControl: true,
    };

    // Add mapId for Advanced Markers
    if (this.useAdvancedMarkers) {
      mapOptions.mapId = "57cd2f58dd3061ffcb52b690";
    }

    this.map = new google.maps.Map(element, mapOptions);
    this.infoWindow = new google.maps.InfoWindow();

    return this.map;
  }

  createMarker(location, type, onClick) {
    const position = {
      lat: parseFloat(location.lat),
      lng: parseFloat(location.lng),
    };

    let marker;

    if (this.useAdvancedMarkers) {
      try {
        marker = new google.maps.marker.AdvancedMarkerElement({
          position: position,
          map: this.map,
          title: location.title,
          content: this.createMarkerContent(type, location),
        });

        marker.addListener("gmp-click", () => onClick && onClick());
      } catch (error) {
        console.warn(
          "⚠️ Advanced Marker failed, using Standard:",
          error.message,
        );
        this.useAdvancedMarkers = false;
        return this.createMarker(location, type, onClick);
      }
    } else {
      const icon = this.createStandardMarkerIcon(type, location);

      marker = new google.maps.Marker({
        position: position,
        map: this.map,
        title: location.title,
        icon: icon,
        optimized: true,
      });

      marker.addListener("click", () => onClick && onClick());
    }

    marker.locationId = location.id;
    this.markers.push(marker);
    return marker;
  }

  createMarkerContent(type, location) {
    const iconElement = document.createElement("div");
    iconElement.style.cursor = "pointer";

    if (type === "store") {
      const img = document.createElement("img");
      img.src = "https://armstrong.co.nz/wp-content/uploads/2025/11/Store.png";
      img.style.width = "40px";
      img.style.height = "40px";
      img.style.objectFit = "contain";
      img.style.display = "block";
      img.alt = "Armstrong Store Location";
      iconElement.appendChild(img);
    } else if (type === "mobile_store") {
      const img = document.createElement("img");
      img.src = "https://armstrong.co.nz/wp-content/uploads/2025/11/Truck.png";
      img.style.width = "40px";
      img.style.height = "40px";
      img.style.objectFit = "contain";
      img.style.display = "block";
      img.alt = "Armstrong Mobile Store";
      iconElement.appendChild(img);
    } else {
      // Region marker
      iconElement.innerHTML = `
                <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="16" cy="16" r="12" fill="#FECC20" stroke="#fff" stroke-width="3"/>
                    <circle cx="16" cy="16" r="4" fill="#fff"/>
                </svg>
            `;
    }

    return iconElement;
  }

  createStandardMarkerIcon(type, location) {
    if (type === "store") {
      return {
        url: "https://armstrong.co.nz/wp-content/uploads/2025/11/Store.png",
        scaledSize: new google.maps.Size(40, 40),
        anchor: new google.maps.Point(20, 40),
      };
    } else if (type === "mobile_store") {
      return {
        url: "https://armstrong.co.nz/wp-content/uploads/2025/11/Truck.png",
        scaledSize: new google.maps.Size(40, 40),
        anchor: new google.maps.Point(20, 40),
      };
    } else {
      return {
        path: google.maps.SymbolPath.CIRCLE,
        fillColor: "#FECC20",
        fillOpacity: 1,
        strokeColor: "#fff",
        strokeWeight: 3,
        scale: 12,
      };
    }
  }

  createPopup(marker, content) {
    this.infoWindow.setContent(content);
    this.infoWindow.open(this.map, marker);
  }

  closePopup() {
    if (this.infoWindow) {
      this.infoWindow.close();
    }
  }

  clearMarkers() {
    this.markers.forEach((marker) => {
      if (this.useAdvancedMarkers) {
        marker.map = null;
      } else {
        marker.setMap(null);
      }
    });
    this.markers = [];
    if (this.infoWindow) {
      this.infoWindow.close();
    }
  }

  setCenter(lat, lng) {
    this.map.setCenter({ lat, lng });
  }

  setZoom(zoom) {
    this.map.setZoom(zoom);
  }

  getCenter() {
    const center = this.map.getCenter();
    return { lat: center.lat(), lng: center.lng() };
  }

  getZoom() {
    return this.map.getZoom();
  }

  flyTo(lat, lng, zoom, callback) {
    const currentCenter = this.getCenter();
    const currentZoom = this.getZoom();

    const deltaLat = lat - currentCenter.lat;
    const deltaLng = lng - currentCenter.lng;
    const deltaZoom = zoom - currentZoom;

    const distance = Math.sqrt(deltaLat * deltaLat + deltaLng * deltaLng);
    const duration = Math.min(800 + distance * 100, 1500);

    const steps = 60;
    const stepDuration = duration / steps;
    let currentStep = 0;

    const easeInOutQuart = (t) => {
      return t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2;
    };

    const interval = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      const easedProgress = easeInOutQuart(progress);

      const newLat = currentCenter.lat + deltaLat * easedProgress;
      const newLng = currentCenter.lng + deltaLng * easedProgress;
      const newZoom = currentZoom + deltaZoom * easedProgress;

      this.map.setCenter({ lat: newLat, lng: newLng });
      this.map.setZoom(newZoom);

      if (currentStep >= steps) {
        clearInterval(interval);
        this.map.setCenter({ lat, lng });
        this.map.setZoom(zoom);
        if (callback) setTimeout(callback, 150);
      }
    }, stepDuration);
  }

  addPolygon(coordinates, options) {
    const paths = coordinates.map((coord) => ({
      lat: coord[1],
      lng: coord[0],
    }));

    this.currentPolygon = new google.maps.Polygon({
      paths: paths,
      strokeColor: options.strokeColor || "#FECC20",
      strokeOpacity: options.strokeOpacity || 0.6,
      strokeWeight: options.strokeWeight || 2,
      fillColor: options.fillColor || "#FECC20",
      fillOpacity: options.fillOpacity || 0.1,
      map: this.map,
    });

    return this.currentPolygon;
  }

  removePolygon(polygon) {
    if (polygon) {
      polygon.setMap(null);
    }
  }

  onIdle(callback) {
    google.maps.event.addListenerOnce(this.map, "idle", callback);
  }

  fitBounds(bounds) {
    const googleBounds = new google.maps.LatLngBounds();
    bounds.forEach((coord) => {
      googleBounds.extend(new google.maps.LatLng(coord[1], coord[0]));
    });
    this.map.fitBounds(googleBounds);
  }

  disableTouchGestures() {
    if (this.map) {
      this.map.setOptions({
        gestureHandling: "none",
        draggable: false,
        zoomControl: false,
      });
    }
  }

  enableTouchGestures() {
    if (this.map) {
      this.map.setOptions({
        gestureHandling: "auto",
        draggable: true,
        zoomControl: true,
      });
    }
  }
}

// Mapbox Provider
class MapboxProvider extends MapProvider {
  constructor() {
    super();
    this.currentPopup = null;
    this.currentPolygon = null;
  }

  async initialize(element, options) {
    const mapId = element.id || "map-" + Date.now();
    console.log("🗺️ Initializing Mapbox for:", mapId);

    // Wait for Mapbox GL JS — it may be deferred by a caching/optimization plugin
    // (e.g. WP Rocket). Polling instead of failing immediately removes the race.
    await this.waitForLibrary(() => !!window.mapboxgl, "Mapbox GL JS");

    if (!window.mapConfig.mapboxToken) {
      throw new Error("Mapbox access token not set");
    }

    // Set token only once globally
    if (!mapboxgl.accessToken) {
      mapboxgl.accessToken = window.mapConfig.mapboxToken;
    }

    // CRITICAL: Clear any existing content in container
    element.innerHTML = "";
    element.style.position = "relative";
    element.style.width = "100%";
    element.style.height = "100%";

    this.map = new mapboxgl.Map({
      container: element,
      style: "mapbox://styles/mapbox/streets-v12",
      center: [options.center.lng, options.center.lat],
      zoom: options.zoom || 5,
      attributionControl: true,
      // FORCE all interactions enabled
      interactive: true,
      cooperativeGestures: false, // Disable "use ctrl+scroll" message
      dragPan: true,
      dragRotate: true,
      scrollZoom: true,
      touchZoomRotate: true,
      touchPitch: true,
      keyboard: true,
      doubleClickZoom: true,
      boxZoom: true,
    });

    // Wait for map to fully load
    await new Promise((resolve) => this.map.on("load", resolve));

    // CRITICAL: Force enable everything again after load
    setTimeout(() => {
      this.map.dragPan.enable();
      this.map.scrollZoom.enable();
      this.map.boxZoom.enable();
      this.map.dragRotate.enable();
      this.map.keyboard.enable();
      this.map.doubleClickZoom.enable();
      this.map.touchZoomRotate.enable();

      // Make container and canvas explicitly interactive
      element.style.pointerEvents = "auto";
      element.style.touchAction = "manipulation";

      const canvas = element.querySelector("canvas");
      if (canvas) {
        canvas.style.pointerEvents = "auto";
        canvas.style.cursor = "grab";
        canvas.style.touchAction = "manipulation";
      }

      console.log("✅ Mapbox", mapId, "fully interactive");
    }, 100);

    return this.map;
  }

  createMarker(location, type, onClick) {
    const el = document.createElement("div");
    el.className = `mapbox-marker mapbox-marker-${type}`;
    el.style.cursor = "pointer";
    el.style.pointerEvents = "auto"; // Ensure clicks work
    el.style.userSelect = "none";

    if (type === "store") {
      el.innerHTML = `<img src="https://armstrong.co.nz/wp-content/uploads/2025/11/Store.png" style="width: 40px; height: 40px; object-fit: contain; pointer-events: none;" alt="Store" />`;
    } else if (type === "mobile_store") {
      el.innerHTML = `<img src="https://armstrong.co.nz/wp-content/uploads/2025/11/Truck.png" style="width: 40px; height: 40px; object-fit: contain; pointer-events: none;" alt="Mobile Store" />`;
    } else {
      // Region marker
      el.innerHTML = `
                <svg width="32" height="32" viewBox="0 0 32 32" style="pointer-events: none;">
                    <circle cx="16" cy="16" r="12" fill="#FECC20" stroke="#fff" stroke-width="3"/>
                    <circle cx="16" cy="16" r="4" fill="#fff"/>
                </svg>
            `;
    }

    const marker = new mapboxgl.Marker(el)
      .setLngLat([parseFloat(location.lng), parseFloat(location.lat)])
      .addTo(this.map);

    if (onClick) {
      console.log(
        `🔗 Attaching click handler to ${type} marker:`,
        location.title,
      );
      el.addEventListener("click", (e) => {
        console.log(`👆 Click event fired on ${type} marker:`, location.title);
        e.stopPropagation();
        onClick();
      });
    }

    marker.locationId = location.id;
    marker.element = el;
    this.markers.push(marker);
    return marker;
  }

  createPopup(marker, content) {
    if (this.currentPopup) {
      this.currentPopup.remove();
    }

    this.currentPopup = new mapboxgl.Popup({
      offset: 25,
      closeButton: true,
      closeOnClick: false,
      focusAfterOpen: false,
      maxWidth: "400px",
      className: "armstrong-popup-interactive",
    })
      .setLngLat(marker.getLngLat())
      .setHTML(content)
      .addTo(this.map);

    // Ensure popup content is clickable
    const popupElement = this.currentPopup.getElement();
    if (popupElement) {
      popupElement.style.pointerEvents = "auto";
      // Make sure links in popup are clickable
      const links = popupElement.querySelectorAll("a");
      links.forEach((link) => {
        link.style.pointerEvents = "auto";
        link.style.cursor = "pointer";
      });
    }
  }

  closePopup() {
    if (this.currentPopup) {
      this.currentPopup.remove();
      this.currentPopup = null;
    }
  }

  clearMarkers() {
    this.markers.forEach((marker) => marker.remove());
    this.markers = [];
    if (this.currentPopup) {
      this.currentPopup.remove();
      this.currentPopup = null;
    }
  }

  setCenter(lat, lng) {
    this.map.setCenter([lng, lat]); // Mapbox uses [lng, lat]
  }

  setZoom(zoom) {
    this.map.setZoom(zoom);
  }

  getCenter() {
    const center = this.map.getCenter();
    return { lat: center.lat, lng: center.lng };
  }

  getZoom() {
    return this.map.getZoom();
  }

  flyTo(lat, lng, zoom, callback) {
    const currentCenter = this.getCenter();
    const currentZoom = this.getZoom();

    // Calculate distance to adjust animation duration
    const deltaLat = lat - currentCenter.lat;
    const deltaLng = lng - currentCenter.lng;
    const distance = Math.sqrt(deltaLat * deltaLat + deltaLng * deltaLng);

    // Dynamic duration based on distance (like Google Maps version)
    const duration = Math.min(1000 + distance * 100, 2000); // 1-2 seconds

    console.log(
      `🎬 Mapbox flyTo: distance=${distance.toFixed(4)}, duration=${duration}ms`,
    );

    this.map.flyTo({
      center: [lng, lat], // Mapbox uses [lng, lat]
      zoom: zoom,
      duration: duration,
      essential: true, // Ensures animation isn't skipped
      easing: (t) => {
        // Smoother easing function (ease-in-out-cubic)
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      },
    });

    if (callback) {
      this.map.once("moveend", callback);
    }
  }

  addPolygon(coordinates, options) {
    const sourceId = "region-boundary-" + Date.now();
    const layerId = sourceId + "-layer";

    this.map.addSource(sourceId, {
      type: "geojson",
      data: {
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: [coordinates],
        },
      },
    });

    this.map.addLayer({
      id: layerId,
      type: "fill",
      source: sourceId,
      paint: {
        "fill-color": options.fillColor || "#FECC20",
        "fill-opacity": options.fillOpacity || 0.1,
      },
    });

    this.map.addLayer({
      id: layerId + "-outline",
      type: "line",
      source: sourceId,
      paint: {
        "line-color": options.strokeColor || "#FECC20",
        "line-width": options.strokeWeight || 2,
        "line-opacity": options.strokeOpacity || 0.6,
      },
    });

    this.currentPolygon = { sourceId, layerId };
    return this.currentPolygon;
  }

  removePolygon(polygon) {
    if (polygon && polygon.layerId) {
      if (this.map.getLayer(polygon.layerId)) {
        this.map.removeLayer(polygon.layerId);
      }
      if (this.map.getLayer(polygon.layerId + "-outline")) {
        this.map.removeLayer(polygon.layerId + "-outline");
      }
      if (this.map.getSource(polygon.sourceId)) {
        this.map.removeSource(polygon.sourceId);
      }
    }
  }

  onIdle(callback) {
    this.map.once("idle", callback);
  }

  fitBounds(bounds) {
    const mapboxBounds = new mapboxgl.LngLatBounds();
    bounds.forEach((coord) => {
      mapboxBounds.extend([coord[0], coord[1]]); // [lng, lat]
    });
    this.map.fitBounds(mapboxBounds, { padding: 50 });
  }

  disableTouchGestures() {
    if (this.map) {
      this.map.dragPan.disable();
      this.map.scrollZoom.disable();
      this.map.touchZoomRotate.disable();
      this.map.touchPitch.disable();
    }
  }

  enableTouchGestures() {
    if (this.map) {
      this.map.dragPan.enable();
      this.map.scrollZoom.enable();
      this.map.touchZoomRotate.enable();
      this.map.touchPitch.enable();
    }
  }
}

// Factory function to create the appropriate provider
function createMapProvider(provider) {
  console.log("🏭 Creating map provider:", provider);

  if (provider === "mapbox") {
    return new MapboxProvider();
  } else {
    return new GoogleMapsProvider();
  }
}

// Export for use in other files
window.createMapProvider = createMapProvider;
window.GoogleMapsProvider = GoogleMapsProvider;
window.MapboxProvider = MapboxProvider;
